import { useCallback } from "react";

import { useAppDispatch } from "@/redux/hooks";

import { useMarkCopywritingReviewedMutation } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.api";
import {
	GenerationJob,
	markJobReviewed,
} from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";
import {
	markFeedPersonCopywritingDone,
	setFeedPanelContextView,
	setFeedSelectedCardId,
	setFeedSelectedPerson,
} from "@/pages/Private/redux/feed/feed.slice";

interface ReviewTarget {
	/** The card holding the job's person — selected so the docked panel opens on it. */
	runId: number;
	/** The card person matching `job.prospectId` — the panel's target (D6). */
	listPersonId: number;
}

/**
 * Review-now consumption of a ready job (PREC-3635 P6 D6): persists the
 * reviewed marker, optimistically clears the ready chip everywhere (card
 * button, row CTA, queue pill) and opens the docked panel on Chat for the
 * job's card/person.
 */
export const useFeedReviewNow = () => {
	const dispatch = useAppDispatch();
	const [markCopywritingReviewed] = useMarkCopywritingReviewedMutation();

	/**
	 * Persist + optimistically clear the ready chip — the shared half of the
	 * flow. Fire-and-forget persist: the optimistic removal is the UI truth;
	 * the mutation's tag invalidation re-syncs the map afterwards.
	 */
	const consumeJob = useCallback(
		(job: GenerationJob) => {
			void markCopywritingReviewed(job.listPersonId);
			dispatch(markJobReviewed(job.prospectId));
			// The consumed job's drafts persist — flip the card person's flag so the
			// reach-out button lands on "drafts done → open chat", not back on idle.
			dispatch(markFeedPersonCopywritingDone(job.listPersonId));
		},
		[dispatch, markCopywritingReviewed]
	);

	/** Full on-page flow: consume + select the job's card + open it on Chat. */
	const reviewJobNow = useCallback(
		(job: GenerationJob, target: ReviewTarget) => {
			consumeJob(job);
			dispatch(setFeedSelectedCardId(target.runId));
			dispatch(setFeedSelectedPerson({ runId: target.runId, listPersonId: target.listPersonId }));
			dispatch(setFeedPanelContextView("chat"));
		},
		[consumeJob, dispatch]
	);

	return { reviewJobNow, consumeJob };
};
